import { useMemo } from 'react';
import { useHub } from '@/contexts/HubContext';
import { currencySymbol, currencyToLocale, formatMoney, formatMoneyCompact } from '@/lib/currency';

const FALLBACK_CURRENCY = 'EUR';

/**
 * Currency formatting bound to the selected operator's `default_currency`.
 * Falls back to EUR when no operator/currency is set. Formatter values are in
 * major units (euros, pounds).
 *
 * `isLoading` is true while the operator context is still resolving — render a
 * skeleton instead of the EUR fallback to avoid a wrong-currency flash.
 */
export function useCurrency() {
  const { selectedOperator, isLoading: hubLoading } = useHub();
  const currency = selectedOperator?.default_currency ?? FALLBACK_CURRENCY;
  const isLoading = !selectedOperator && hubLoading;

  return useMemo(
    () => ({
      currency,
      isLoading,
      locale: currencyToLocale(currency),
      symbol: currencySymbol(currency),
      format: (value: number) => formatMoney(value, currency),
      formatCompact: (value: number, decimals?: number) => formatMoneyCompact(value, currency, decimals),
    }),
    [currency, isLoading]
  );
}
